import { useQuery } from "@tanstack/react-query";
import { Package, ShoppingCart, DollarSign, AlertTriangle } from "lucide-react";
import { supabase } from "@/supabase";
import AdminLayout from "@/components/admin/AdminLayout";
import AuthGuard from "@/components/admin/AuthGuard";

const AdminDashboard = () => {
  const { data: stats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => {
      const [productsRes, ordersRes, lowStockRes] = await Promise.all([
        supabase.from("products").select("id", { count: "exact" }),
        supabase.from("orders").select("id, total_price, order_status"),
        supabase.from("products").select("id", { count: "exact" }).lte("stock_quantity", 3).gt("stock_quantity", 0),
      ]);

      const orders = ordersRes.data || [];
      const totalRevenue = orders
        .filter((o) => o.order_status !== "cancelled")
        .reduce((sum, o) => sum + Number(o.total_price), 0);
      const pendingOrders = orders.filter((o) => o.order_status === "pending").length;

      return {
        totalProducts: productsRes.count || 0,
        totalOrders: orders.length,
        totalRevenue,
        pendingOrders,
        lowStockProducts: lowStockRes.count || 0,
      };
    },
  });

  const { data: recentOrders } = useQuery({
    queryKey: ["recent-orders"],
    queryFn: async () => {
      const { data } = await supabase
        .from("orders")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(5);
      return data || [];
    },
  });

  const statCards = [
    {
      label: "Total de Produtos",
      value: stats?.totalProducts || 0,
      icon: Package,
      color: "text-primary",
      bg: "bg-primary/10",
    },
    {
      label: "Total de Pedidos",
      value: stats?.totalOrders || 0,
      icon: ShoppingCart,
      color: "text-golden-dark",
      bg: "bg-golden/10",
    },
    {
      label: "Receita Total",
      value: `R$ ${(stats?.totalRevenue || 0).toFixed(2).replace(".", ",")}`,
      icon: DollarSign,
      color: "text-green-600",
      bg: "bg-green-100",
    },
    {
      label: "Estoque Baixo",
      value: stats?.lowStockProducts || 0,
      icon: AlertTriangle,
      color: "text-amber-600",
      bg: "bg-amber-100",
    },
  ];

  const statusLabels: Record<string, { label: string; class: string }> = {
    pending: { label: "Pendente", class: "bg-amber-100 text-amber-700" },
    paid: { label: "Pago", class: "bg-green-100 text-green-700" },
    preparing: { label: "Preparando", class: "bg-blue-100 text-blue-700" },
    shipped: { label: "Enviado", class: "bg-purple-100 text-purple-700" },
    delivered: { label: "Entregue", class: "bg-green-100 text-green-700" },
    cancelled: { label: "Cancelado", class: "bg-red-100 text-red-700" },
  };

  return (
    <AuthGuard>
      <AdminLayout>
        <div className="mb-8">
          <h1 className="font-display text-3xl font-semibold text-foreground">
            Dashboard
          </h1>
          <p className="text-muted-foreground mt-1">
            Visão geral da sua loja
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat) => (
            <div key={stat.label} className="admin-card">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${stat.bg}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                  <p className="text-2xl font-bold text-foreground">{stat.value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Orders */}
        <div className="admin-card">
          <h2 className="font-display text-xl font-semibold text-foreground mb-4">
            Pedidos Recentes
          </h2>
          {recentOrders && recentOrders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Cliente
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Produto
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Valor
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((order) => (
                    <tr key={order.id} className="border-b border-border last:border-0">
                      <td className="py-3 px-4 text-sm">{order.customer_name}</td>
                      <td className="py-3 px-4 text-sm">{order.product_name}</td>
                      <td className="py-3 px-4 text-sm">
                        R$ {Number(order.total_price).toFixed(2).replace(".", ",")}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            statusLabels[order.order_status]?.class || ""
                          }`}
                        >
                          {statusLabels[order.order_status]?.label || order.order_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Nenhum pedido ainda.</p>
          )}
        </div>
      </AdminLayout>
    </AuthGuard>
  );
};

export default AdminDashboard;
